/**
 * @file src/room/paramLease.js
 * @description Who currently has a hand on a given control — folded from the relay's ordered
 * stream, so every client answers the question identically without anyone refereeing it.
 *
 * A grant says WHO MAY drive a turntable, and several people may hold the same one. That is the
 * right resolution for a session and the wrong one for a knob: two hands reaching for one knob at
 * once produce a fight nobody wins, because each client would apply whichever write happened to
 * arrive last on ITS socket. The fix is not to narrow the grant, it is to notice that a gesture has
 * a beginning and an end and let the first hand keep the control until it lets go.
 *
 * That signal is already on the wire. Every parameter write is stamped `live` (the hand is moving)
 * or `commit` (the hand let go), so the FIRST `live` on a free control opens a hold, further `live`
 * messages from the holder extend it, and the `commit` closes it. Nothing new is announced; a lease
 * is just a reading of the traffic that was already there.
 *
 * Two properties matter more than the rules themselves:
 *
 * **It is a fold, not a negotiation.** Every client sees the same messages in the same order (the
 * relay's `rseq`) and runs this same code, so they arrive at the same holder without exchanging a
 * single message about it. There is no lease server, no lock request, no host — consistent with a
 * room having no leader at all.
 *
 * **Expiry is read, never fired.** A hold ends 600 ms after its last `live`, measured on the
 * relay's clock (`rt`) — but nothing here counts down. `holderOf` is asked for an instant and
 * answers for that instant, so a client whose timers were throttled to nothing in a background tab
 * catches up to exactly the same answer as everyone else the moment it looks again. A countdown
 * would not: a missed tick is a hold that stayed open on one machine and closed on another, and
 * from there the two disagree about who is driving with no way back. It is also what lets a
 * revoked grant leave an open gesture alone (the hold outlives the revocation, and expires from
 * the stream like any other) without that grace becoming a divergence.
 */

/**
 * How long a hold survives its last `live` message, on the relay clock.
 *
 * Long enough to bridge the gaps in an ordinary gesture — live writes are already thinned before
 * they are published, and a slow drag plus a hiccup on the wire can leave a few hundred ms between
 * two of them, so a shorter window would drop a hold the hand never let go of. Short enough that a
 * hand that vanished (a tab closed mid-drag, a `commit` lost) frees the control before anyone
 * decides it is stuck.
 */
export const HOLD_MS = 600;

/**
 * The identity of one control: which turntable, which parameter, which dimension of it.
 *
 * The dimension is part of the identity because it is part of the control — the same axis knob
 * shows a different value per dimension, and two people on the same axis in DIFFERENT dimensions
 * are not reaching for the same thing.
 */
export function leaseKey(wireVoiceId, paramName, dimensionId) {
  return `${wireVoiceId} ${paramName} ${dimensionId ?? ''}`;
}

/**
 * A hold ledger: fold ordered events in, ask who holds what.
 *
 * State per control is one open hold or nothing — a queue of waiting hands would be a promise the
 * room cannot keep, since the waiter may have moved on by the time the control frees.
 */
export function createLeaseLedger({ holdMs = HOLD_MS } = {}) {
  /** @type {Map<string, { clientId: string, lastLiveRt: number }>} */
  const holds = new Map();

  /** Is this hold still open at `rt`? */
  const openAt = (hold, rt) => hold != null && rt - hold.lastLiveRt < holdMs;

  return {
    /**
     * Fold ONE relayed parameter write. Call in relay order, for every client's writes INCLUDING
     * our own echo — a sender that skipped its own messages would be folding a different stream
     * from everyone else, and would be the one client that disagreed about who is driving.
     *
     * @param {{ key: string, clientId: string, intent: 'live'|'commit', rt: number }} event
     * @returns {boolean} whether this write is the holder's (and so should be applied). A write
     *   from anyone else while a hold is open is refused — every client refuses the same one.
     */
    apply({ key, clientId, intent, rt }) {
      if (typeof key !== 'string' || typeof clientId !== 'string' || !clientId) return false;
      if (!Number.isFinite(rt)) return false;

      const current = holds.get(key);
      const held = openAt(current, rt);
      if (held && current.clientId !== clientId) return false; // someone else's hand is on it

      if (intent === 'live') {
        // Opens the hold, or extends the one already ours.
        holds.set(key, { clientId, lastLiveRt: rt });
        return true;
      }

      // `commit` ends the gesture. A write that never had one — a keypad entry, a reset — is a
      // commit too: on a free control it passes and opens nothing, so a single value can never
      // leave a knob nobody is touching looking held.
      if (held) holds.delete(key);
      return true;
    },

    /** Is anything held at all? O(1), and the answer is no almost all of the time. */
    get size() {
      return holds.size;
    },

    /** Who has a hand on this control at `rt`, or null when it is free. */
    holderOf(key, rt) {
      const hold = holds.get(key);
      return openAt(hold, rt) ? hold.clientId : null;
    },

    /** May `clientId` write this control at `rt`? True when free or already theirs. */
    canWrite(key, clientId, rt) {
      const holder = this.holderOf(key, rt);
      return holder === null || holder === clientId;
    },

    /**
     * Every open hold at `rt`, as one stable string: `key=holder` pairs, sorted.
     *
     * The whole answer in a form two moments can be compared by. Callers use it to notice that the
     * answer changed — which is a different event from a message arriving, since a hold ends
     * because time passed and nothing announces that.
     */
    openHolders(rt) {
      const parts = [];
      for (const [key, hold] of holds) {
        if (openAt(hold, rt)) parts.push(`${key}=${hold.clientId}`);
      }
      return parts.sort().join('\n');
    },

    /**
     * When the earliest still-open hold expires, or null when nothing is open.
     *
     * A caller may use this to know when to LOOK again — the answers this ledger gives change at
     * that instant even though no message arrives to announce it. Looking late is harmless (the
     * answer for a given instant never changes); not looking at all only leaves a control drawn as
     * held slightly longer than it was.
     */
    nextExpiryRt(rt) {
      let soonest = null;
      for (const hold of holds.values()) {
        if (!openAt(hold, rt)) continue;
        const expiry = hold.lastLiveRt + holdMs;
        if (soonest === null || expiry < soonest) soonest = expiry;
      }
      return soonest;
    },

    /**
     * Forget every hold a client had. A member who left cannot let go of anything, and their holds
     * would otherwise sit out their full window on a control nobody is touching.
     */
    forget(clientId) {
      for (const [key, hold] of holds) {
        if (hold.clientId === clientId) holds.delete(key);
      }
    },

    /** Drop everything (room teardown / tests). */
    clear() {
      holds.clear();
    },
  };
}
